import { isAfter } from 'date-fns';
import Swal, { type SweetAlertOptions } from "sweetalert2";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Download, AlertTriangle } from 'lucide-react';
import AprilPicto from '@/components/shared/april-picto';
import { PictoContrats, PictoConformite } from '@/assets/icons-april-fr/pictos';
import APILink from "@/components/shared/api-link";
import { useContractsData, useSelectedContract } from '@/hooks/api/use-contracts';
import { getLastReceipt, getRcCircuitLink, useContractReceipts } from '@/hooks/api/use-receipts';
import { hasContractApporteur43397 as hasContractApporteur43397Fn, isActionAvailable } from '@/lib/contracts';
import { toDate } from '@/lib/dates';
import type { Contract } from '@/types/api/contract';
import { PDF } from '@/types/api/endpoints';
import FormReleveInformation from "@/pages/questions/liste-questions/components/releve-information/form-releve-information";

const ListeDocuments = () => {
    /* Rendu sous `SelectContract`, qui n'affiche son contenu qu'une fois le contrat chargé. */
    const contract = (useSelectedContract() ?? {}) as Contract;
    const quittance = getLastReceipt(useContractReceipts(contract.id).data);
    const hasContractApporteur43397 = hasContractApporteur43397Fn(useContractsData());

    const isTransitoire = contract?.old_vehicule && contract?.vehicule?.transitoire === true && contract?.vehicule?.vente_old_vehicule === false && isAfter(toDate(contract?.old_vehicule?.date_fin_piece), new Date());

    const handleClickInfo = () => {
        void Swal.fire({
            icon: '',
            title: '',
            text: 'Ce véhicule bénéficie des mêmes garanties et options que celles de votre nouveau véhicule, uniquement s\'il est mis en circulation pour des essais en vue de la vente et pour une durée maximale de 30 jours.',
            showConfirmButton: true,
            confirmButtonText: 'Ok',
            showCloseButton: true
        } as unknown as SweetAlertOptions);
    };

    const showDocuments = !['6', '5', 'I'].includes(contract.situation?.statutCode as string) && quittance;
    const code = (contract.vehicule?.codeProduit || '').toUpperCase();
    const isNVEIorVAE = code.includes('NVEI') || code.includes('VAE') || code.includes('TROT') || code.includes('VELO');
    const hasMemo = Boolean(showDocuments && quittance?.memo_link && !!contract.vehicule!.immatriculation && !isNVEIorVAE);
    const hasAttestation = Boolean(showDocuments && quittance?.attestation_assurance_link);
    const rcCircuitLink = getRcCircuitLink(contract, quittance);
    const hasRcCircuit = Boolean(showDocuments && rcCircuitLink);
    const hasRI = isActionAvailable(contract, 'editer_ri') && !(contract.vehicule?.codeProduit ?? '').startsWith('229127_');

    return (
        <div className="mt-6">
            <div className="space-y-6">
                {/* Documents contractuels */}
                <Card data-tutorial="documents-card">
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <AprilPicto icon={PictoContrats} size="md" />
                            Mes documents contractuels
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        {hasMemo || hasAttestation || hasRcCircuit ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {hasMemo && (
                                    <div className="p-6 rounded-lg border bg-muted/30 text-center transition-all duration-200 hover:bg-primary/10 hover:border-primary/30">
                                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mx-auto mb-4">
                                            <AprilPicto icon={PictoContrats} size="lg" />
                                        </div>
                                        <p className="font-semibold text-sm mb-1">Mémo Véhicule Assuré</p>
                                        <p className="text-xs text-muted-foreground mb-4">
                                            Votre justificatif d’assurance avec toutes les informations de votre contrat
                                            au format numérique.
                                        </p>
                                        <APILink
                                            href={quittance!.memo_link as string}
                                            className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary-hover transition-all duration-300 ease-in-out cursor-pointer no-underline"
                                        >
                                            <Download className="w-4 h-4" />
                                            Télécharger
                                        </APILink>
                                    </div>
                                )}

                                {hasAttestation && (
                                    <div className="p-6 rounded-lg border bg-muted/30 text-center transition-all duration-200 hover:bg-primary/10 hover:border-primary/30">
                                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mx-auto mb-4">
                                            <AprilPicto icon={PictoConformite} size="lg" />
                                        </div>
                                        <p className="font-semibold text-sm mb-1">Attestation d'assurance</p>
                                        <p className="text-xs text-muted-foreground mb-4">
                                            Votre attestation qui vous permet de voyager et de vous déplacer en toute sécurité.
                                        </p>
                                        <APILink
                                            href={quittance!.attestation_assurance_link as string}
                                            className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary-hover transition-all duration-300 ease-in-out cursor-pointer no-underline"
                                        >
                                            <Download className="w-4 h-4" />
                                            Télécharger
                                        </APILink>
                                    </div>
                                )}

                                {hasRcCircuit && (
                                    <div className="p-6 rounded-lg border bg-muted/30 text-center transition-all duration-200 hover:bg-primary/10 hover:border-primary/30">
                                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mx-auto mb-4">
                                            <AprilPicto icon={PictoConformite} size="lg" />
                                        </div>
                                        <p className="font-semibold text-sm mb-1">Attestation RC Roulage Circuit</p>
                                        <p className="text-xs text-muted-foreground mb-4">
                                            Votre attestation pour le roulage circuit.
                                        </p>
                                        <APILink
                                            href={rcCircuitLink as string}
                                            className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary-hover transition-all duration-300 ease-in-out cursor-pointer no-underline"
                                        >
                                            <Download className="w-4 h-4" />
                                            Télécharger
                                        </APILink>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">Aucun document disponible pour ce contrat.</p>
                        )}
                    </CardContent>
                </Card>

                {/* Relevé de sinistres */}
                {hasRI && (
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <AprilPicto icon={PictoContrats} size="md" />
                                Relevé de sinistres
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <p className="text-sm text-muted-foreground mb-4">
                                Pour télécharger votre <strong className="text-foreground">Relevé de sinistres</strong>,
                                sélectionnez un motif puis cliquez sur le bouton ci-dessous :
                            </p>
                            <FormReleveInformation riLink={PDF.releveInformation(contract.id)} title="Editer mon relevé de sinistres" contractId={contract.id} hasContractApporteur43397={hasContractApporteur43397} />
                        </CardContent>
                    </Card>
                )}

                {/* Assurance transitoire */}
                {isTransitoire && (
                    <Card className="border-warning/30">
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-warning" />
                                Assurance transitoire
                                <span className="text-base font-normal text-muted-foreground">
                                    — {contract.old_vehicule?.marque} {contract.old_vehicule?.modele}{' '}
                                    {contract.old_vehicule?.immatriculation}
                                </span>
                                <button
                                    onClick={handleClickInfo}
                                    className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-sm cursor-pointer"
                                >
                                    ?
                                </button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <p className="text-sm text-muted-foreground mb-6">
                                L'assurance transitoire est actuellement activée sur ce véhicule. Pour télécharger le
                                Mémo Véhicule Assuré cliquez sur le bouton ci-dessous.
                            </p>
                            {contract.old_vehicule?.memo_link && (
                                <APILink
                                    href={contract.old_vehicule.memo_link}
                                    className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary-hover transition-all duration-300 ease-in-out cursor-pointer no-underline"
                                >
                                    <Download className="w-4 h-4" />
                                    Télécharger le Mémo Véhicule Assuré
                                </APILink>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default ListeDocuments;
